import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { Expression } from "abreact/expression/Expression";
import { GuidedExpressionEditorUtils } from "abreact/expression/ui/editor/impl/utils/GuidedExpressionEditorUtils";
import { ExpressionStructure } from "abreact/expression/ui/editor/readonly/ExpressionStructure";
import { SummaryElement, SummaryElementType } from "abreact/expression/ui/editor/readonly/ExpressionSummaryElement";
import { ExpressionSummaryModel, SummaryMode } from "abreact/expression/ui/editor/readonly/ExpressionSummaryModel";
import { ExpExpressionNodeNodeTypeEnum } from "abweb/generated/abwebinternal/api/dto/ExpExpressionNode";
import { ObjectUtils } from "abweb/utils/ObjectUtils";
export class ExpressionStructureNode {
  constructor(type, expressionDomainModel, value, parent, leafNodeOperatorID, isSingleFireAction, rootNodeType) {
    _defineProperty(this, "type", void 0);
    _defineProperty(this, "value", void 0);
    _defineProperty(this, "children", []);
    _defineProperty(this, "parent", void 0);
    _defineProperty(this, "_expressionDomainModel", void 0);
    _defineProperty(this, "_rootNodeType", void 0);
    _defineProperty(this, "isAdvanced", false);
    /*
    If this is just a leaf node (operand to an operator), then this is the operator that
    has the operand this represents.
     In this scenario, this would be "min" only and the leafNodeOperatorID would be "is_in _range", which is only used by old ReadOnlyOperandDisplay stuff
    is_in _range
      min: 33
      max: 44
     */
    _defineProperty(this, "leafNodeOperatorID", void 0);
    _defineProperty(this, "isSingleFireAction", void 0);
    this.type = type;
    this.value = value;
    this.parent = parent;
    this._expressionDomainModel = expressionDomainModel;
    this.leafNodeOperatorID = leafNodeOperatorID;
    this.isSingleFireAction = !!isSingleFireAction;
    this._rootNodeType = rootNodeType;
    this.computeAdvanced();
  }
  get expressionDomainModel() {
    return this._expressionDomainModel;
  }
  static createRootNode(expressionDomainModel, basis) {
    const rootNodeType = basis.rootNode ? basis.rootNode.nodeType : basis.nodeType;
    return new ExpressionStructureNode("ROOT", expressionDomainModel, undefined, undefined, undefined, undefined, rootNodeType);
  }
  get rootNodeType() {
    return this._rootNodeType;
  }
  get parentType() {
    return this.parent?.type;
  }
  getExpOperatorOrThrow(expExpressionNode) {
    let operator;
    if (this.leafNodeOperatorID) {
      operator = this._expressionDomainModel.getExpOperator(this.leafNodeOperatorID);
    }
    if (!operator) {
      if (expExpressionNode.operatorID) {
        operator = this._expressionDomainModel.getExpOperator(expExpressionNode.operatorID);
      }
      if (!operator) {
        throw new Error("Invalid operator id: " + expExpressionNode.operatorID);
      }
    }
    return operator;
  }
  isLeafForOperator() {
    return this.leafNodeOperatorID != null;
  }
  contribute(model, summaryMode, isEmbeddedExpression) {
    switch (this.type) {
      case "ROOT":
        this.children.forEach(childNode => {
          childNode.contribute(model, summaryMode, isEmbeddedExpression);
        });
        break;
      case "ACTION_LIST":
        this.children.forEach((childNode, index) => {
          if (!this.isSingleFireAction || index === 0) {
            model.currentRow.addElement(SummaryElement.LIST_BULLET);
          } else if (index > 0) {
            model.currentRow.addElement(SummaryElement.LIST_BULLET_ELSE);
          }
          childNode.contribute(model, summaryMode);
          if (index < this.children.length - 1) {
            model.startNewRow();
          }
        });
        break;
      case "CONNECTOR":
        !this.isSubExpressionRoot(this.value) && model.incrementDepth();
        if (this.children.length === 1) {
          this.children[0].contribute(model, summaryMode);
        } else if (this.children.length > 0) {
          this.children.forEach((childNode, position) => {
            // paren in front of first condition but no parens at root level
            if (position === 0 && model.depth > 1) {
              model.currentRow.addElement(SummaryElement.OPEN_PAREN);
            }
            /*
             Never emit a connector for the first row
             xyz equals zzz
             AND zzz is specified
             */
            if (position > 0) {
              // only start a new row for 1->n, not 0 since and not if this is an embedded expression (which
              // just gets spread out on the same line
              !isEmbeddedExpression && model.startNewRow();
              model.currentRow.addElement(this.getModelElementForConnectorValue());
            }
            childNode.contribute(model, summaryMode, isEmbeddedExpression);
          });
          // no parens at root level
          if (model.depth > 1) {
            model.currentRow.addElement(SummaryElement.CLOSE_PAREN);
          }
        }
        !this.isSubExpressionRoot(this.value) && model.decrementDepth();
        break;
      case "LEAF":
        if (this.isLeafForOperator()) {
          this.contributeArgumentOperandNodeForOperator(model, summaryMode);
        } else {
          // just being defensive in case the operator is not passed in
          model.currentRow.addElement(new SummaryElement(SummaryElementType.LEAF, this.value));
        }
        break;
      case "OPERATOR":
        model.currentRow.operatorNode = this;
        if (this.value?.operatorID) {
          this.contributeOperator(model, this.value, summaryMode, this.value.operatorID);
          if (this.children) {
            this.children
            /*
            sub-expression roots are already processed as CONNECTOR nodes, so don't also add them here.
             */.filter(child => !this.isSubExpressionRoot(child.value)).forEach(child => child.contribute(model, summaryMode, true));
          }
        }
        break;
    }
  }
  isSubExpressionRoot(expExpressionNode) {
    return expExpressionNode?.categoryID === Expression.Domain.Constants.CATEGORY_SUB_EXPRESSION;
  }
  contributeOperator(model, expExpressionNode, summaryMode, parentOperatorID) {
    const customGenerator = ExpressionSummaryModel.getApplicableCustomOperatorContributor(expExpressionNode);
    if (customGenerator) {
      return customGenerator.contributeOperator(model, expExpressionNode, summaryMode, this);
    } else {
      let elements = this.computeTemplateElements(expExpressionNode, summaryMode);
      elements.forEach(element => {
        if (element.options?.startsNewLine) {
          // set teh rendering depth according to the options
          model.renderDepthAmplification = element.options?.indentLevel ?? 0;
          // if there is not alredy a customSubExpressionDepthAmplification (perhaps from a custom renderer)
          // set it equal to the depth amplification -1 to ensure the sub expression aligns at the indent level
          if (model.renderDepthAmplification > 0 && model.customSubExpressionDepthAmplification === 0) {
            model.customSubExpressionDepthAmplification = model.renderDepthAmplification - 1;
          }
          model.startNewRow();
        }
        if (element.isToken) {
          const node = this.findExpExpressionNodeByOperandName(element, expExpressionNode);
          if (node) {
            let options = element.options;
            if (parentOperatorID != null) {
              options = this.computeMergedOperatorAndOperandOptions(parentOperatorID, node.operandName);
            }
            switch (node.nodeType) {
              case ExpExpressionNodeNodeTypeEnum.OPERATOR:
                if (this.isSubExpressionRoot(node) && parentOperatorID)
                  // parent will always be provided in this case
                  {
                    this.contributeSubExpression(model, node, summaryMode, parentOperatorID, options);
                  } else {
                  this.contributeOperator(model, node, summaryMode, node.operatorID);
                }
                break;
              case ExpExpressionNodeNodeTypeEnum.LITERAL:
              case ExpExpressionNodeNodeTypeEnum.REFERENCE:
                this.contributeLiteralOrReference(model, expExpressionNode.operatorID, node, options);
                break;
            }
          }
        } else {
          model.currentRow.addElement(new SummaryElement(SummaryElementType.TEXT, element.value, element.options));
        }
      });
    }
  }
  contributeSubExpression(model, expExpressionNode, summaryMode, parentOperatorID, expSummaryElementOptions) {
    let elements = this.computeOperandTemplateElements(parentOperatorID, expExpressionNode);
    for (let element of elements) {
      if (element.isToken) {
        /*
        For sub-expressions, create a new structure entirely and then let
        the root node contribute to the current model
        */
        const subExpressionStructure = new ExpressionStructure(this._expressionDomainModel);
        subExpressionStructure.initFromExpressionNode(ObjectUtils.deepThaw(expExpressionNode), expExpressionNode.operatorID);
        try {
          if (expSummaryElementOptions != null && expSummaryElementOptions.indentLevel != null && expSummaryElementOptions.startsNewLine) {
            model.renderDepthAmplification = expSummaryElementOptions.indentLevel;
          } else {
            model.renderDepthAmplification = model.customSubExpressionDepthAmplification;
          }
          if (element.options?.startsNewLine) {
            model.startNewRow();
          }
          subExpressionStructure.rootNode?.contribute(model, summaryMode,
          // if a render depth for the sub expressions has been set, then this is should not be rendered as a single-line embedded expression
          model.renderDepthAmplification === 0 && model.customSubExpressionDepthAmplification === 0);
        } finally {
          model.renderDepthAmplification = 0;
          model.customSubExpressionDepthAmplification = 0;
        }
      } else {
        if (element.options?.startsNewLine) {
          model.startNewRow();
        }
        model.currentRow.addElement(new SummaryElement(SummaryElementType.TEXT, element.value, element.options));
      }
    }
  }
  contributeLiteralOrReference(model, operatorID, operandNode, outerExpSummaryElementOptions) {
    if (outerExpSummaryElementOptions?.startsNewLine) {
      model.startNewRow();
      if (outerExpSummaryElementOptions.indentLevel != null) {
        model.renderDepthAmplification = outerExpSummaryElementOptions.indentLevel;
      }
    }
    let elements = this.computeOperandTemplateElements(operatorID, operandNode);
    elements.forEach(element => {
      if (element.options?.startsNewLine) {
        model.startNewRow();
        if (element.options?.indentLevel != null) {
          model.renderDepthAmplification = element.options.indentLevel;
        }
      }
      if (element.isToken) {
        if (operandNode) {
          model.currentRow.addElement(new SummaryElement(SummaryElementType.LEAF, operandNode, element.options));
        }
      } else {
        model.currentRow.addElement(new SummaryElement(SummaryElementType.TEXT, element.value, element.options));
      }
    });
  }

  /*
  Need to figure out how to handle advanced. Create a STRUCTURE model that is ONLY advanced and then
  generate as usual. Walk each advanced and create a structure node and then have it contribute to a new model?
   */
  contributeArgumentOperandNodeForOperator(model, summaryMode) {
    let operatorID = this.leafNodeOperatorID;
    let operandNode = this.value;
    let elements = this.computeOperandTemplateElements(operatorID, operandNode);
    elements.forEach(element => {
      if (element.isToken) {
        if (operandNode.nodeType === ExpExpressionNodeNodeTypeEnum.OPERATOR) {
          this.contributeOperator(model, operandNode, summaryMode);
        } else {
          model.currentRow.addElement(new SummaryElement(SummaryElementType.LEAF, operandNode, element.options));
        }
      } else {
        model.currentRow.addElement(new SummaryElement(SummaryElementType.TEXT, element.value, element.options));
      }
    });
  }
  findExpExpressionNodeByOperandName(element, node) {
    let theNode;
    if (node?.operandValues) {
      for (let expNode of node.operandValues) {
        if (expNode.operandName) {
          let operandName = expNode.operandName;
          if (element.value === operandName || element.isGroup && element.groupOperatorNames.indexOf(operandName) > -1) {
            theNode = expNode;
            break;
          }
        }
      }
    }
    return theNode;
  }
  computeTemplateElements(node, summaryMode) {
    let template = this.getSummaryTemplate(node, summaryMode);
    return this.parseTemplateIntoElements(template, node.operatorID);
  }
  computeMergedOperatorAndOperandOptions(operatorID, operandName) {
    let options;
    let operandElementOptions;
    if (operandName) {
      const expArgumentOperand = this._expressionDomainModel.findExpArgumentOperandForExpOperator(operatorID, operandName);
      if (expArgumentOperand) {
        const expSummaryFormat = this.findOperandSummaryTemplate(expArgumentOperand);
        operandElementOptions = expSummaryFormat && this.findElementOptionsForElementName(expArgumentOperand.name, expSummaryFormat);
      }
      const operator = this._expressionDomainModel.getExpOperator(operatorID);
      const operatorSummaryFormat = operator.summaryOutputFormats && operator.summaryOutputFormats.find(f => f.summaryType === Expression.Domain.Constants.FUNCTION_SUMMARY_STANDARD);
      let elementOptionDefaultsFromOperator = operatorSummaryFormat && this.findElementOptionsForElementName(operandName, operatorSummaryFormat);

      /*
      If the operator options did nto directly reference this operand, check to see if the group it belongs to was referenced and
      use those
      */
      if (!elementOptionDefaultsFromOperator && expArgumentOperand?.groupID) {
        elementOptionDefaultsFromOperator = operatorSummaryFormat && this.findElementOptionsForElementName(expArgumentOperand.groupID, operatorSummaryFormat);
      }
      options = operandElementOptions ?? {};
      if (elementOptionDefaultsFromOperator != null) {
        /*
        Walk the values in the operator and use them as defaults for any values no already set in teh
        operand level options
         */
        for (const key of Object.keys(elementOptionDefaultsFromOperator)) {
          if (options[key] === undefined) {
            options[key] = elementOptionDefaultsFromOperator[key];
          }
        }
      }
    }
    return options;
  }
  computeOperandTemplateElements(operatorID, operandNode) {
    let expOperatorSummaryFormatForOperand = this.getOperandSummaryTemplate(operatorID, operandNode);
    const mergedExpSummaryElementOptionsForOperand = this.computeMergedOperatorAndOperandOptions(operatorID, operandNode.operandName);
    if (expOperatorSummaryFormatForOperand) {
      // Make sure this doesn't mutate the object in teh domain
      expOperatorSummaryFormatForOperand = ObjectUtils.deepCopy(expOperatorSummaryFormatForOperand);
      const expArgumentOperand = this._expressionDomainModel.findExpArgumentOperandForExpOperator(operatorID, operandNode.operandName);
      /*
      These are options that may have been specified at the operator level and which should be inherited by the
      template for the operand
       */
      if (mergedExpSummaryElementOptionsForOperand) {
        let newOptions = {};
        if (expOperatorSummaryFormatForOperand.elementOptions) {
          newOptions = ObjectUtils.deepThaw(expOperatorSummaryFormatForOperand.elementOptions);
        }

        // To make sure that there is also an option entry for the operand (not just .this) add one for the oeprand
        newOptions[operandNode.operandName] = mergedExpSummaryElementOptionsForOperand;

        // To make sure that there is also an option entry for the operand's group add one
        if (expArgumentOperand?.groupID) {
          newOptions[expArgumentOperand?.groupID] = mergedExpSummaryElementOptionsForOperand;
        }
        expOperatorSummaryFormatForOperand = {
          summaryType: expOperatorSummaryFormatForOperand.summaryType,
          template: expOperatorSummaryFormatForOperand.template,
          elementOptions: newOptions
        };
      }
      return this.parseTemplateIntoElements(expOperatorSummaryFormatForOperand, operatorID);
    } else {
      return [this.createTokenTemplateElementDescriptor(operandNode.operandName, operatorID, mergedExpSummaryElementOptionsForOperand)];
    }
  }
  // TODO ADD A PROPERTY TO CONTROL LOOKING FOR .THIS TOO
  findElementOptionsForElementName(name, expOperatorSummaryFormat) {
    let options;
    if (expOperatorSummaryFormat.elementOptions) {
      options = expOperatorSummaryFormat.elementOptions[name];
    }
    return options;
  }
  parseTemplateIntoElements(expOperatorSummaryFormat, operatorID) {
    let elements = [];
    let chars = [...expOperatorSummaryFormat.template];
    let currChar = "";
    let prevChar = "";
    let currElement = "";
    let currOptions;
    let inToken = false;
    for (let c of chars) {
      currChar = c;
      if (!inToken && currChar !== "$" && currChar !== "{") {
        currElement += currChar;
      }
      if (prevChar === "$" && currChar === "{") {
        inToken = true;
        if (currElement.trim()) {
          elements.push({
            value: currElement.trim(),
            isToken: false
          });
        }
        currElement = "";
        prevChar = currChar;
        continue;
      }
      if (inToken) {
        if (currChar === "}") {
          inToken = false;
          const elementTokenName = currElement.trim();
          currOptions = this.findElementOptionsForElementName(elementTokenName, expOperatorSummaryFormat);
          if (currOptions?.textValue) {
            elements.push({
              value: currOptions.textValue,
              isToken: false,
              options: currOptions
            });
          } else {
            elements.push(this.createTokenTemplateElementDescriptor(elementTokenName, operatorID, currOptions));
          }
          currElement = "";
        } else {
          currElement += currChar;
        }
      }
      prevChar = currChar;
    }
    if (!inToken && currElement.trim()) {
      elements.push({
        value: currElement.trim(),
        isToken: false
      });
    }
    return elements;
  }
  createTokenTemplateElementDescriptor(token, operatorID, expSummaryElementOptions) {
    let templateDescriptor = {
      value: token,
      isToken: true,
      options: expSummaryElementOptions
    };
    if (!this._expressionDomainModel.findExpArgumentOperandForExpOperator(operatorID, token)) {
      if (this._expressionDomainModel.findExpArgumentOperandGroup(operatorID, token)) {
        templateDescriptor.isGroup = true;
        let operator = this._expressionDomainModel.getExpOperator(operatorID);
        if (operator.argumentOperands) {
          templateDescriptor.groupOperatorNames = operator.argumentOperands.filter(op => op.groupID === token).map(op => op.name);
        }
      }
    }
    return templateDescriptor;
  }
  getSummaryTemplate(expExpressionNode, summaryMode) {
    let standardTemplate;
    let advancedTemplate = this.createAdvancedTemplateForOperatorNode(expExpressionNode);
    let summaryFormat = this._expressionDomainModel.getExpOperatorSummaryFormat(expExpressionNode.operatorID, Expression.Domain.Constants.FUNCTION_SUMMARY_STANDARD);
    if (summaryFormat) {
      standardTemplate = summaryFormat;
    } else {
      standardTemplate = this.createDefaultTemplate(expExpressionNode);
    }
    return this.computeCorrectTemplateForMode(summaryMode, standardTemplate, advancedTemplate);
  }
  computeCorrectTemplateForMode(summaryMode, standardTemplate, advancedTemplate) {
    let expOperatorSummaryFormatToUse;
    switch (summaryMode) {
      case SummaryMode.ADVANCED_ONLY:
        expOperatorSummaryFormatToUse = {
          summaryType: Expression.Domain.Constants.FUNCTION_SUMMARY_STANDARD,
          template: advancedTemplate
        };
        break;
      case SummaryMode.ALL_OPERANDS:
        expOperatorSummaryFormatToUse = standardTemplate;
        if (expOperatorSummaryFormatToUse.template.length > 0 && advancedTemplate.length > 0) {
          expOperatorSummaryFormatToUse = {
            summaryType: Expression.Domain.Constants.FUNCTION_SUMMARY_STANDARD,
            template: `${standardTemplate.template} | ${advancedTemplate}`,
            elementOptions: standardTemplate.elementOptions
          };
        }
        break;
      case SummaryMode.EXCLUDE_ADVANCED:
        expOperatorSummaryFormatToUse = standardTemplate;
        break;
      default:
        throw new Error("Unknown SummaryMode: " + summaryMode);
    }
    return expOperatorSummaryFormatToUse;
  }
  findOperandSummaryTemplate(expArgumentOperand) {
    let expOperatorSummaryFormat;
    if (expArgumentOperand.summaryOutputFormats) {
      for (let expSummaryFormat of expArgumentOperand.summaryOutputFormats) {
        if (expSummaryFormat?.summaryType === Expression.Domain.Constants.FUNCTION_SUMMARY_STANDARD) {
          expOperatorSummaryFormat = expSummaryFormat;
          break;
        }
      }
    }
    if (expOperatorSummaryFormat) {
      if (expOperatorSummaryFormat.template.indexOf(ExpressionStructureNode.OPERAND_TEMPLATE_THIS_REFERENCE) > -1) {
        let newOptions;
        if (expOperatorSummaryFormat.elementOptions) {
          // TODO CLONE SPECIFICALLY??
          newOptions = ObjectUtils.deepThaw(expOperatorSummaryFormat.elementOptions);

          // ensure that options mapped to .this are found for the operand name
          if (newOptions[ExpressionStructureNode.OPERAND_THIS_REFERENCE]) {
            newOptions[expArgumentOperand.name] = expOperatorSummaryFormat.elementOptions[ExpressionStructureNode.OPERAND_THIS_REFERENCE];
          }
        }
        const newTemplate = expOperatorSummaryFormat.template.replace(ExpressionStructureNode.OPERAND_TEMPLATE_THIS_REFERENCE, `\$\{${expArgumentOperand.name}\}`);
        expOperatorSummaryFormat = {
          summaryType: expOperatorSummaryFormat.summaryType,
          template: newTemplate,
          elementOptions: newOptions
        };
      }
    }
    return expOperatorSummaryFormat;
  }
  operandHasSummaryTemplate(expArgumentOperand) {
    return this.findOperandSummaryTemplate(expArgumentOperand) != null;
  }
  getOperandSummaryTemplate(operatorID, expExpressionNode) {
    let expOperatorSummaryFormat;
    if (expExpressionNode.operandName) {
      let expArgumentOperand = this._expressionDomainModel.findExpArgumentOperandForExpOperator(operatorID, expExpressionNode.operandName);
      if (expArgumentOperand) {
        expOperatorSummaryFormat = this.findOperandSummaryTemplate(expArgumentOperand);

        /*
        If rendering just an operand in isolation, then the operator template will not be constructed
        Need to generate a template just for the operand, like "Min ${min}"
        */
        if (!expOperatorSummaryFormat && this.isLeafForOperator()) {
          let expArgumentOperand = this._expressionDomainModel.findExpArgumentOperandForExpOperator(operatorID, expExpressionNode.operandName);
          if (expArgumentOperand) {
            expOperatorSummaryFormat = this.generateExpOperatorSummaryFormatForOperand(expArgumentOperand, expExpressionNode);
          }
        }
      }
    }
    return expOperatorSummaryFormat;
  }
  getExpExpressionNodeForOperandName(operandName, operatorNode) {
    let expExpressionNode = undefined;
    if (this.isLeafForOperator() != null && operandName === this.value?.operandName) {
      return this.value;
    }
    if (operatorNode.operandValues) {
      for (let operandNode of operatorNode.operandValues) {
        if (operandNode.operandName === operandName) {
          expExpressionNode = operandNode;
        }
      }
    }
    return expExpressionNode;
  }
  createDefaultTemplate(expExpressionNode) {
    let template = "";
    let operator = this.getExpOperatorOrThrow(expExpressionNode);
    if (operator.sourceOperandTypes && operator.sourceOperandTypes.length > 0) {
      let sourceOperandName = this._expressionDomainModel.getSourceOperandName(operator);
      if (operator.sourceOperandLabel) {
        template += operator.sourceOperandLabel + " ";
      }
      if (this.getExpExpressionNodeForOperandName(sourceOperandName, expExpressionNode) != null) {
        template += "${" + sourceOperandName + "}";
      }
    }
    if (Expression.Domain.OperatorRole.isRowBased(operator.roleID) && !this.isLeafForOperator()) {
      template += ` ${operator.label}`;
    }
    if (operator?.argumentOperands) {
      operator.argumentOperands.filter(ao => !ao.advanced).filter(ao => !this.isLeafForOperator() || ao.name === expExpressionNode.operandName).forEach(argumentOperand => {
        let opTemplate = this.generateExpOperatorSummaryFormatForOperand(argumentOperand, expExpressionNode);
        if (opTemplate) {
          if (template.length > 0) {
            template += " ";
          }
          template += opTemplate.template;
        }
      });
    }
    return {
      summaryType: "standard",
      template: template
    };
  }
  createAdvancedTemplateForOperatorNode(expExpressionNode) {
    let template = "";
    let numAdvancedOperandsAdded = 0;
    let operator = this.getExpOperatorOrThrow(expExpressionNode);
    if (operator?.argumentOperands) {
      operator.argumentOperands.filter(ao => ao.advanced).filter(ao => !this.isLeafForOperator() || ao.name === expExpressionNode.operandName).forEach(argumentOperand => {
        let opSummaryFormat = this.generateExpOperatorSummaryFormatForOperand(argumentOperand, expExpressionNode);
        if (opSummaryFormat.template !== "") {
          if (numAdvancedOperandsAdded > 0) {
            template += " | ";
          }
          template += opSummaryFormat.template;
          numAdvancedOperandsAdded++;
        }
      });
    }
    return template;
  }
  generateExpOperatorSummaryFormatForOperand(argumentOperand, expExpressionNode) {
    let template = "";
    let hasSummaryTemplate = this.operandHasSummaryTemplate(argumentOperand);
    let argNode = this.getExpExpressionNodeForOperandName(argumentOperand.name, expExpressionNode);
    if (argNode) {
      if (GuidedExpressionEditorUtils.getPrefixLabelIfProvided(argumentOperand) && !hasSummaryTemplate) {
        template += GuidedExpressionEditorUtils.getPrefixLabelIfProvided(argumentOperand) + " ";
      }
      template += "${" + argNode.operandName + "}";
      if (GuidedExpressionEditorUtils.getSuffixLabelIfProvided(argumentOperand) && !hasSummaryTemplate) {
        template += " " + GuidedExpressionEditorUtils.getSuffixLabelIfProvided(argumentOperand);
      }
    }
    return {
      template,
      summaryType: Expression.Domain.Constants.FUNCTION_SUMMARY_STANDARD
    };
  }
  getModelElementForConnectorValue() {
    if (this.value?.operatorID === Expression.Domain.Constants.AND_OPERATOR_ID) {
      return SummaryElement.AND;
    } else if (this.value?.operatorID === Expression.Domain.Constants.OR_OPERATOR_ID) {
      return SummaryElement.OR;
    } else {
      throw new Error(`Unrecognized connector value: ${this.value}`);
    }
  }
  computeAdvanced() {
    if (this.type === "LEAF" && this.value?.operandName) {
      let operatorID = this.leafNodeOperatorID;
      if (!operatorID && this.parentType === "OPERATOR") {
        operatorID = this.parent?.value?.operatorID;
      }
      if (operatorID) {
        let operand = this._expressionDomainModel.findExpArgumentOperandForExpOperator(operatorID, this.value.operandName);
        this.isAdvanced = operand?.advanced ?? false;
      }
    }
  }
}
_defineProperty(ExpressionStructureNode, "OPERAND_THIS_REFERENCE", ".this");
_defineProperty(ExpressionStructureNode, "OPERAND_TEMPLATE_THIS_REFERENCE", "${.this}");
//# sourceMappingURL=ExpressionStructureNode.js.map
